import { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, Search } from 'lucide-react';
import Input from '@/components/ui/input.jsx';

function normalizeText(value) {
  return String(value ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export const OperationalCombobox = forwardRef(function OperationalCombobox(
  {
    id,
    label,
    value,
    options = [],
    placeholder = 'Buscar...',
    loading = false,
    loadingLabel = 'Cargando...',
    emptyLabel = 'Sin resultados',
    disabled = false,
    className,
    inputClassName,
    listClassName,
    onChange,
    onCommitNext,
    onFocus,
    onBlur,
  },
  forwardedRef,
) {
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (typeof forwardedRef === 'function') {
      forwardedRef(inputRef.current);
      return undefined;
    }

    if (forwardedRef && typeof forwardedRef === 'object') {
      forwardedRef.current = inputRef.current;
    }

    return undefined;
  }, [forwardedRef]);

  const selectedOption = useMemo(
    () => options.find((option) => String(option?.value ?? '') === String(value ?? '')) || null,
    [options, value],
  );

  const selectedLabel = selectedOption?.label || '';

  const filteredOptions = useMemo(() => {
    const normalizedQuery = normalizeText(query);
    if (!normalizedQuery) return options;

    return options.filter((option) => {
      const searchable = normalizeText(option?.searchText || [option?.label, option?.helperText, option?.value].filter(Boolean).join(' '));
      return searchable.includes(normalizedQuery);
    });
  }, [options, query]);

  const closeDropdown = () => {
    setIsOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };

  const openDropdown = () => {
    if (disabled) return;
    setIsOpen(true);
    setQuery('');
    setActiveIndex(selectedOption ? options.findIndex((option) => String(option?.value ?? '') === String(value ?? '')) : -1);
  };

  const commitOption = (option) => {
    if (!option || disabled) return;
    onChange?.(option.value, option);
    closeDropdown();
    window.requestAnimationFrame(() => {
      onCommitNext?.(option);
    });
  };

  const handleFocus = (event) => {
    if (disabled) return;
    openDropdown();
    onFocus?.(event);
  };

  const handleBlur = (event) => {
    onBlur?.(event);
    window.setTimeout(() => {
      if (!rootRef.current?.contains(document.activeElement)) {
        closeDropdown();
      }
    }, 0);
  };

  const handleChange = (event) => {
    if (disabled) return;
    setQuery(event.target.value);
    if (!isOpen) setIsOpen(true);
    setActiveIndex(0);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'Escape') {
      if (isOpen) {
        event.preventDefault();
        closeDropdown();
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) openDropdown();
      setActiveIndex((prev) => (prev < 0 ? 0 : Math.min(filteredOptions.length - 1, prev + 1)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) openDropdown();
      setActiveIndex((prev) => (prev < 0 ? Math.max(filteredOptions.length - 1, 0) : Math.max(0, prev - 1)));
      return;
    }

    if (event.key === 'Enter') {
      if (!isOpen) {
        return;
      }

      event.preventDefault();
      const option = filteredOptions[activeIndex] || filteredOptions[0] || null;
      commitOption(option);
    }
  };

  const displayedValue = isOpen ? query : selectedLabel;
  const currentActiveIndex = activeIndex < 0 ? -1 : Math.min(activeIndex, Math.max(filteredOptions.length - 1, 0));

  return (
    <div ref={rootRef} className={clsx('relative', className)}>
      {label ? <label className="mb-0.5 block text-[11px] font-semibold uppercase tracking-[0.18em] text-[rgba(var(--fg),0.55)]">{label}</label> : null}
      <div className="relative">
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[rgba(var(--fg),0.42)]" />
        <Input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isOpen}
          aria-controls={id ? `${id}-listbox` : undefined}
          value={displayedValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={clsx('pl-9 pr-9', inputClassName)}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={isOpen ? 'Cerrar opciones' : 'Abrir opciones'}
          disabled={disabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => (isOpen ? closeDropdown() : openDropdown())}
          className="absolute right-2 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-[rgba(var(--fg),0.55)] transition hover:bg-[rgba(var(--subpanel),0.72)] hover:text-[rgb(var(--fg))] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ChevronDown size={15} className={clsx('transition-transform', isOpen ? 'rotate-180' : 'rotate-0')} />
        </button>
      </div>

      {isOpen ? (
        <div
          id={id ? `${id}-listbox` : undefined}
          role="listbox"
          className={clsx(
            'absolute left-0 right-0 z-30 mt-1 max-h-72 overflow-auto rounded-xl border border-[rgba(var(--border),0.48)] bg-[rgba(var(--panel),0.98)] p-1 shadow-[0_16px_36px_rgba(var(--shadow),0.18)]',
            listClassName,
          )}
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-[rgba(var(--fg),0.62)]">{loadingLabel}</div>
          ) : filteredOptions.length ? (
            filteredOptions.map((option, index) => (
              <button
                key={String(option.value)}
                type="button"
                role="option"
                aria-selected={index === currentActiveIndex}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => commitOption(option)}
                className={clsx(
                  'flex w-full flex-col items-start rounded-lg px-3 py-2 text-left text-sm transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]',
                  index === currentActiveIndex
                    ? 'bg-[rgba(var(--brand),0.12)] text-[rgb(var(--fg))]'
                    : 'hover:bg-[rgba(var(--subpanel),0.7)] text-[rgb(var(--fg))]',
                )}
              >
                <span className="font-medium">{option.label}</span>
                {option.helperText ? <span className="mt-0.5 text-[11px] text-[rgba(var(--fg),0.58)]">{option.helperText}</span> : null}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-sm text-[rgba(var(--fg),0.62)]">{emptyLabel}</div>
          )}
        </div>
      ) : null}
    </div>
  );
});

export default OperationalCombobox;
